import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, MoreVertical, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePageHeader } from "@/hooks/usePageHeader";
import { VehicleForm } from "@/components/vehicles/VehicleForm";
import { VehiclePhotos } from "@/components/vehicles/VehiclePhotos";
import { VehicleDocumentsTab } from "@/components/vehicles/VehicleDocumentsTab";
import { VehicleServiceTab } from "@/components/vehicles/VehicleServiceTab";
import { VehicleDamagesTab } from "@/components/vehicles/VehicleDamagesTab";
import { VehicleBookingsTab } from "@/components/vehicles/VehicleBookingsTab";
import {
  VehicleDeadlineBadges,
  VehicleStatusBadge,
} from "@/components/vehicles/VehicleBadges";
import {
  useDeleteVehicle,
  useUpdateVehicle,
  useVehicle,
} from "@/hooks/useVehicles";

const VEHICLE_TABS = [
  "information",
  "bookings",
  "documents",
  "service",
  "damages",
] as const;
type VehicleTab = (typeof VEHICLE_TABS)[number];

const VehicleDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: vehicle, isLoading } = useVehicle(id);
  const updateVehicle = useUpdateVehicle();
  const deleteVehicle = useDeleteVehicle();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const tabParam = searchParams.get("tab");
  const currentTab: VehicleTab = VEHICLE_TABS.includes(tabParam as VehicleTab)
    ? (tabParam as VehicleTab)
    : "information";
  const handleTabChange = (value: string) =>
    setSearchParams({ tab: value }, { replace: true });

  const openDamages =
    vehicle?.damages?.filter((damage) => damage.status === "open").length ?? 0;

  const headerTabs = useMemo(
    () => [
      { value: "information", label: "Information" },
      { value: "bookings", label: "Bookings" },
      { value: "documents", label: "Documents" },
      { value: "service", label: "Service" },
      {
        value: "damages",
        label: openDamages ? `Damages (${openDamages})` : "Damages",
      },
    ],
    [openDamages],
  );

  usePageHeader({
    title: vehicle?.name,
    tabs: headerTabs,
    currentTab,
    onTabChange: handleTabChange,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Loading vehicle...</p>
      </div>
    );
  }

  if (!vehicle || !id) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate("/vehicles")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Vehicles
        </Button>
        <p className="text-muted-foreground">Vehicle not found.</p>
      </div>
    );
  }

  const subtitle = [
    vehicle.licensePlate,
    [vehicle.brand, vehicle.model].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/vehicles")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold">{vehicle.name}</h1>
              <VehicleStatusBadge status={vehicle.status} />
            </div>
            {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
            <div className="flex flex-wrap gap-1">
              <VehicleDeadlineBadges vehicle={vehicle} />
            </div>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              data-testid="vehicle-actions-menu"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              offlineEdit
              onClick={() => setIsDeleteOpen(true)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Tabs value={currentTab} onValueChange={handleTabChange}>
        <TabsList>
          {headerTabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="information" className="mt-6 space-y-6">
          <Card>
            <CardContent className="pt-6">
              <VehiclePhotos vehicleId={id} images={vehicle.images ?? []} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Vehicle Details</CardTitle>
            </CardHeader>
            <CardContent>
              <VehicleForm
                vehicle={vehicle}
                submitLabel="Save"
                isPending={updateVehicle.isPending}
                onSubmit={(input) => updateVehicle.mutate({ id, ...input })}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bookings" className="mt-6">
          <VehicleBookingsTab vehicleId={id} />
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <VehicleDocumentsTab
            vehicleId={id}
            documents={vehicle.documents ?? []}
          />
        </TabsContent>

        <TabsContent value="service" className="mt-6">
          <VehicleServiceTab
            vehicleId={id}
            records={vehicle.serviceRecords ?? []}
          />
        </TabsContent>

        <TabsContent value="damages" className="mt-6">
          <VehicleDamagesTab vehicleId={id} damages={vehicle.damages ?? []} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete „{vehicle.name}“?</AlertDialogTitle>
            <AlertDialogDescription>
              The vehicle is removed from the vehicle list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteVehicle.mutate(id, {
                  onSuccess: () => navigate("/vehicles"),
                })
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VehicleDetailPage;
